import React, { useState, useEffect } from "react";
import axios from "axios";
import useInfiniteImageLoad from "./useInfiniteImageLoad";

const ImageGrid = ({ initialImages, totalCount, formToken, user, editType }) => {
  const { images: hookImages, loading, hasMore } = useInfiniteImageLoad(initialImages, totalCount, formToken);
  const [images, setImages] = useState(initialImages);

  useEffect(() => {
    setImages(hookImages);
  }, [hookImages]);

  const [completedImageId, setCompletedImageId] = useState(null);

  const handleSetAsAction = async (imageId) => {
    let payload;
    if (editType === 'avatar') {
      payload = { user: { avatar_id: imageId } };
    } else if (editType === 'mobile_background') {
      payload = { user: { mobile_background_id: imageId } };
    } else {
      payload = { user: { background_id: imageId } };
    }

    try {
      const response = await axios.patch(
        `/api/v1/users/${user.id}.json`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${formToken}`,
            'Content-Type': 'application/json',
          },
        }
      );
      setCompletedImageId(imageId);
    } catch (error) {
      console.error(`Error setting ${editType}:`, error);
    }
  };

  const handleShareImage = async (imageId) => {
    try {
      const response = await axios.patch(
        `/api/v1/chat_images/${imageId}.json`,
        { chat_image: { public: !images.find(img => img.id === imageId).public } },
        {
          headers: {
            Authorization: `Bearer ${formToken}`,
            'Content-Type': 'application/json'
          },
        }
      );
      const updatedImage = response.data.chat_image;
      setImages(prevImages => prevImages.map(img =>
        img.id === updatedImage.id ? updatedImage : img
      ));
    } catch (error) {
      console.error("Error toggling image visibility:", error);
    }
  };

  const downloadImage = (imageUrl) => {
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = 'tyb_image.png';
    link.click();
  };

  const getEditTypeLabel = () => {
    switch (editType) {
      case 'avatar': return 'Avatar';
      case 'mobile_background': return 'Mobile Background';
      case 'background_image': return 'Desktop Background';
      default: return 'Background';
    }
  };

  const getEditTypeColor = () => {
    switch (editType) {
      case 'avatar': return 'from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600';
      case 'mobile_background': return 'from-green-500 to-teal-500 hover:from-green-600 hover:to-teal-600';
      default: return 'from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600';
    }
  };

  return (
    <div className="max-w-screen-xl mx-auto">
      {images.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <p>No images yet!</p>
        </div>
      ) : (
        <div className="columns-2 sm:columns-3 lg:columns-4 gap-4 space-y-4">
          {images.map((image) => (
            <div
              key={`image-${image.id}`}
              className="group relative rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 bg-white break-inside-avoid"
            >
              {/* Image - preserves natural aspect ratio */}
              <img
                className="w-full h-auto group-hover:scale-105 transition-transform duration-300"
                src={image.imageUrl}
                alt="Your creation"
              />

              {/* Hover overlay with actions */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-3">
                {/* Icon buttons row */}
                <div className="flex items-center justify-center gap-2 mb-2">
                  {/* Download button */}
                  <button
                    onClick={() => downloadImage(image.imageUrl)}
                    className="p-2 bg-white/90 hover:bg-white rounded-full shadow-lg transition-all hover:scale-110"
                    title="Download"
                  >
                    <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                  </button>

                  {/* Share button - only show if user owns this image */}
                  {user.id === image.createdUser && image.role === 'assistant' && (
                    <button
                      onClick={() => handleShareImage(image.id)}
                      className={`p-2 rounded-full shadow-lg transition-all hover:scale-110 ${
                        image.public
                          ? 'bg-green-500 hover:bg-green-600'
                          : 'bg-white/90 hover:bg-white'
                      }`}
                      title={image.public ? 'Shared! Click to hide' : 'Share with everyone'}
                    >
                      <svg
                        className={`w-5 h-5 ${image.public ? 'text-white' : 'text-gray-700'}`}
                        fill={image.public ? 'currentColor' : 'none'}
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              {/* Selection mode - prominent select button or current indicator */}
              {editType && (() => {
                const isCurrentSelection =
                  (editType === 'avatar' && user.avatar_id === image.id) ||
                  (editType === 'mobile_background' && user.mobile_background_id === image.id) ||
                  (editType === 'background_image' && user.background_id === image.id);

                if (isCurrentSelection) {
                  // Show "Current" badge for the currently selected image
                  return (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <div className={`px-4 py-2 rounded-xl font-semibold shadow-lg bg-gradient-to-r ${getEditTypeColor()} text-white flex items-center gap-2`}>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Current {getEditTypeLabel()}
                      </div>
                    </div>
                  );
                }

                // Show select button for other images
                return (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <button
                      onClick={() => handleSetAsAction(image.id)}
                      disabled={completedImageId === image.id}
                      className={`px-4 py-2 rounded-xl font-semibold shadow-lg transition-all transform hover:scale-105 ${
                        completedImageId === image.id
                          ? 'bg-gray-400 text-white cursor-not-allowed'
                          : `bg-gradient-to-r ${getEditTypeColor()} text-white`
                      }`}
                    >
                      {completedImageId === image.id ? (
                        <span className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Selected!
                        </span>
                      ) : (
                        `Use as ${getEditTypeLabel()}`
                      )}
                    </button>
                  </div>
                );
              })()}

              {/* Shared badge */}
              {image.public && !editType && (
                <div className="absolute top-2 right-2 bg-green-500 text-white text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                  Shared
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {loading && (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
        </div>
      )}

      {hasMore && !loading && (
        <div id="loading-element" className="py-8 text-center text-gray-400 text-sm">
          Scroll for more...
        </div>
      )}
    </div>
  );
};

export default ImageGrid;
